import type { FormikHelpers } from 'formik';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import type { DonationFundFragment, RegistryItemFragment } from '@graphql/fragments/registry.graphql.generated';
import { CustomItemDataType, DonationFundPlatformTypeEnum } from '@graphql/generated-base';
import {
  useAddRegistryItemPhotoMutation,
  useCreateDonationFundPlatformMutation,
  useCreateRegistryItemMutation,
  useUpdateRegistryItemMutation,
  useupdateRegistryItemQuantityMutation,
} from '@graphql/mutations/registry.graphql.generated';
import { useTranslation as useTranslationReact } from 'react-i18next';
import { useTranslation } from '@shared/core/i18n';
import type { EditGiftDailogFields } from './types';
import type { CookedProduct } from '@apps/registry/common/selectors/ProductListSelector';
import { isPricedProduct } from '@apps/registry/common/selectors/ProductListSelector';
import type { OnRemoveRegistryItemHandler } from '@apps/registry/admin/Registry.types';
import { getCurrencySymbolFromCurrencyCode } from '@shared/utils/currency/utils';
import { DEFAULT_CURRENCY_SYMBOL } from '@shared/utils/currency/constants';
import { useToast } from '@withjoy/joykit';
import { uploadedImageAssetId, useImageUpload } from '@shared/components/UploadWidget/useImageUpload';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMount } from '@shared/utils/hooks/useMount';
import { useTimeout } from '@shared/hooks/useTimeout';
import { useSharedRegistryTranslations } from '@apps/registry/SharedRegistry.i18n';
import type { PaymentMethodOption } from '@shared/utils/paymentMethodOptions';
import { paymentMethodOptions } from '@shared/utils/paymentMethodOptions';
import type { DonationFundInfoFragmentWithUrl } from '@apps/registry/common/components/Catalog/components/ProductGrid/ProductGrid.controller';
import {
  createPaymentMetadata,
  findDonationFundPlatform,
  getExistingPlatform,
  getPaymentLink,
  normalizePaymentUsername,
} from '@apps/registry/common/components/AddCashFund/helpers';
import { useIdentify } from '@shared/core';
import { useAdminRegistryTelemetry } from '@apps/registry/admin/AdminRegistry.telemetry';
import { useTrackImpactRadius } from '@shared/utils/impactRadius/useTrackImpactRadius';
import { useCatalogRouting } from '@apps/registry/common/components/Catalog/useCatalogRouting';
import axios from 'axios';
import { useShopStorefrontContext } from '@apps/registry/common/components/Catalog/components/ShopStorefrontProvider';
import { RegistryItemSource } from '@apps/registry/common/components/Catalog/Catalog.constants';
import { useAppPaymentDisclaimerDialogController } from '@apps/registry/common/components/AddCashFund/AppPaymentDisclaimerDialog.controller';
import { isValidUrlAndDomain } from '@shared/utils/urlHelpers';
import { addError, addSuppressedError } from '@shared/utils/logger';
import { PARAGRAPH_MAX_LENGTH } from '@shared/constants/textLimits';

type EditDialogControllerProps = Readonly<{
  eventId: string;
  product: CookedProduct | null;
  showAddressReminderDialog?: () => void;
  /**
   * Fired with the new item after it is successfully created (not on edits), before
   * `onClose`. Lets an add surface run its own post-add confirmation — e.g. the shop
   * search bar opening the RecentlyAddedRegistryItemUpdateDrawer.
   */
  onRegistryItemCreated?: (item: RegistryItemFragment) => void;
  onRemove?: OnRemoveRegistryItemHandler;
  isOpen: boolean;
  showCurrencyDialog: () => void;
  onClose: () => void;
  donationFundInfo?: DonationFundInfoFragmentWithUrl;
  prefilledUrl?: string;
}>;

export type ExtractedProduct = Readonly<{
  name: string;
  price: string;
  currency: string;
  /** Symbol */
  currencyRaw: string;
  /** MSRP */
  regularPrice: string;
  url: string;
  mainImage: Readonly<{
    url: string;
  }>;
}>;

export type PurchaseOption = 'cash' | 'groupGift' | 'external';
export const useEditGiftDialogController = ({
  eventId,
  product,
  onRemove,
  onClose,
  showAddressReminderDialog,
  onRegistryItemCreated,
  showCurrencyDialog,
  donationFundInfo,
  prefilledUrl,
}: EditDialogControllerProps) => {
  const { t: translateForm } = useTranslationReact('generalFormValidation');
  const { t, tq } = useTranslation('sharedRegistry');
  const { linkInvalid, quantityMinError, receivedQuantityError } = t('giftDialog');

  const { getCashFundDialogTranslations } = useSharedRegistryTranslations();
  const { selectPaymentEmptyError, invalidUsernameIsEmailError, invalidUsernameError } = getCashFundDialogTranslations();

  const { identify } = useIdentify();

  const [createDonationFundPlatform] = useCreateDonationFundPlatformMutation();
  const trackImpactRadius = useTrackImpactRadius();

  const { isBabyStorefront } = useShopStorefrontContext();

  const [updatedPhoto, setUpdatedPhoto] = useState<string | undefined>(undefined);
  const [photoFilename, setPhotoFilename] = useState<string | undefined>(undefined);
  const [isPaymentMethodWarningShown, setIsPaymentMethodWarningShown] = useState<boolean>(false);
  const [prefilledData, setPrefilledData] = useState<ExtractedProduct>();
  const [isProductExtractionLoading, setIsProductExtractionLoading] = useState(false);
  const [showExtractError, setShowExtractError] = useState(false);

  const [createRegistryItem, { loading: createRegistryItemLoading }] = useCreateRegistryItemMutation({
    refetchQueries: () => ['GetAdminRegistryRegistries', 'GetRegistryOnboardingSuggestions', 'RegistryGiftBasketCountByEventName'],
    awaitRefetchQueries: true,
  });
  const [updateRegistryItem, { loading: updateRegistryItemLoading }] = useUpdateRegistryItemMutation({
    refetchQueries: () => ['GetAdminRegistryRegistries'],
  });

  const [updateRegistryItemTotalQuantity, { loading: updateRegistryItemQuantityLoading }] = useupdateRegistryItemQuantityMutation();

  const [uploadPhoto] = useAddRegistryItemPhotoMutation({
    refetchQueries: () => ['GetAdminRegistryRegistries'],
  });
  const qtyReceived = (product?.reserved || 0) + (product?.purchased || 0);

  const { editRegistryItemSaveButton, manualProductDataExtractionAttempt, clickAddRegistryItem } = useAdminRegistryTelemetry();

  useEffect(() => {
    setIsPaymentMethodWarningShown(false);
  }, [product?.id]);

  const { goToBrandPage } = useCatalogRouting();

  const handleBrandClick = useCallback(() => {
    if (product?.brand) {
      const route = isBabyStorefront ? '/shop/baby/brand' : '/shop/brand';
      goToBrandPage(`${route}/${product?.brand.toLowerCase()}`);
    }
  }, [goToBrandPage, isBabyStorefront, product?.brand]);

  const hasAttemptedDataExtraction = useRef(false);
  const abortControllerRef = useRef<AbortController>();
  const errorDismissTimeout = useTimeout();

  const extractDataFromUrl = useCallback(
    async (url: string) => {
      if (!url?.trim() || product?.id) return;

      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setPrefilledData(undefined);
      setIsProductExtractionLoading(true);
      hasAttemptedDataExtraction.current = true;
      let success = false;
      let resp = undefined;

      try {
        // extract Product data from the URL / page to gain initial input values
        resp = await axios.get<ExtractedProduct>('/api/product/extract', {
          params: { url },
          signal: controller.signal,
        });

        setPrefilledData({ ...resp.data, regularPrice: resp.data.regularPrice || resp.data.price });
        success = true;
      } catch (error) {
        if (axios.isCancel(error)) return;
        setShowExtractError(true);
        errorDismissTimeout.set(() => {
          setShowExtractError(false);
        }, 5000);
      } finally {
        if (controller.signal.aborted) return;
        let domain = 'unknown';
        try {
          domain = new URL(url).hostname;
        } catch (urlError) {
          addSuppressedError(urlError, { url, source: 'extractDataFromUrl' });
        }
        manualProductDataExtractionAttempt({
          domain,
          success,
          productExtractedData: (resp?.data as ExtractedProduct) || undefined,
        });
        setIsProductExtractionLoading(false);
      }
    },
    [errorDismissTimeout, manualProductDataExtractionAttempt, product?.id],
  );

  useEffect(() => {
    if (prefilledUrl) {
      extractDataFromUrl(prefilledUrl);
    }
  }, [extractDataFromUrl, prefilledUrl]);

  useMount(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  });

  const handleSubmit = async (editGiftDialogFields: EditGiftDailogFields, actions: FormikHelpers<EditGiftDailogFields>) => {
    setIsPaymentMethodWarningShown(false);
    const priceToFix = editGiftDialogFields.price ? +editGiftDialogFields.price : 0;

    try {
      if (product?.id) {
        const productId = product.id;
        if (product.requested !== editGiftDialogFields.qty) {
          await updateRegistryItemTotalQuantity({
            variables: {
              id: productId,
              quantity: editGiftDialogFields.qty,
            },
          });
        }

        const normalizedUsername = normalizePaymentUsername(editGiftDialogFields.paymentMethod, editGiftDialogFields.username);
        const paymentLink = getPaymentLink(editGiftDialogFields.paymentMethod, normalizedUsername);

        const getUpdateGiftVariables = (platformId?: Maybe<string>) => ({
          id: productId,
          payload: {
            totalRequested: editGiftDialogFields.qty,
            isGroupGiftingEnabled: editGiftDialogFields.qty === 1 ? editGiftDialogFields.isGroupGiftingEnabled : false,
            note: editGiftDialogFields.note,
            isGetCashEquivalent: editGiftDialogFields.isGetCashEquivalentEnabled,
            donationFundPayload:
              editGiftDialogFields.isGroupGiftingEnabled && product?.numberPrice && editGiftDialogFields.qty === 1
                ? {
                    eventId,
                    fundType: CustomItemDataType.cash,
                    platformId,
                    title: product.title,
                    url: paymentLink,
                  }
                : undefined,
            custom: isCatalogItem
              ? undefined
              : {
                  type: CustomItemDataType.gift,
                  title: editGiftDialogFields.title || '',
                  url: editGiftDialogFields.link || '',
                  priceAsFloatingPointDecimalString: priceToFix.toFixed(2),
                },
            mustHave: editGiftDialogFields.mustHave,
          },
        });

        const platform = product.donationFund?.fund.platform;
        const existingPlatform = findDonationFundPlatform({ paymentMethod: editGiftDialogFields.paymentMethod, username: normalizedUsername }, paymentLink, donationFundInfo);

        const currentPlatformMatches =
          !!platform?.id && platform.type === editGiftDialogFields.paymentMethod && platform.username === normalizedUsername && platform.link === paymentLink;

        const shouldCreateNewPlatform = !!(editGiftDialogFields.isGroupGiftingEnabled && !existingPlatform && !currentPlatformMatches);

        if (shouldCreateNewPlatform) {
          const newPlatform = await createDonationFundPlatform({
            variables: {
              eventId: eventId,
              type: editGiftDialogFields.paymentMethod as DonationFundPlatformTypeEnum,
              link: paymentLink,
              username: normalizedUsername,
            },
          });
          if (newPlatform) {
            const newPlatformData = newPlatform.data?.createDonationFundPlatform;
            await updateRegistryItem({
              variables: getUpdateGiftVariables(newPlatformData?.id),
            });
          } else {
            await updateRegistryItem({
              variables: getUpdateGiftVariables(platform?.id),
            });
          }
        } else {
          await updateRegistryItem({
            variables: getUpdateGiftVariables(existingPlatform ? existingPlatform.id : platform?.id),
          });
        }
        if (photoFilename && product.id) {
          await uploadPhoto({ variables: { assetId: photoFilename, id: productId } });
        }
      } else {
        const newRegistryItem = await createRegistryItem({
          variables: {
            eventId: eventId,
            payload: {
              totalRequested: editGiftDialogFields.qty,
              note: editGiftDialogFields.note,
              isGetCashEquivalent: editGiftDialogFields.isGetCashEquivalentEnabled,
              custom: {
                type: CustomItemDataType.gift,
                title: editGiftDialogFields.title || '',
                url: editGiftDialogFields.link || '',
                priceAsFloatingPointDecimalString: priceToFix.toFixed(2),
                imageUrlForImport: editGiftDialogFields.image || '',
              },
              metadata: {
                source: 'manualScanned',
                sourceMetadata: {
                  scannedPrice: prefilledData?.price || '0',
                },
              },
            },
          },
        });
        const createdItem = newRegistryItem.data?.createRegistryItem;
        const id = createdItem?.id;
        if (photoFilename && id) {
          await uploadPhoto({ variables: { assetId: photoFilename, id } });
        }
        trackImpactRadius('Product_Added');
        actions.resetForm();
        clearPhoto();
        if (showAddressReminderDialog) {
          showAddressReminderDialog();
        }
        if (createdItem) {
          onRegistryItemCreated?.(createdItem);
        }
        identify({ addedGift: true });
        clickAddRegistryItem(
          priceToFix * 100,
          '$',
          editGiftDialogFields.qty,
          editGiftDialogFields.link || '',
          editGiftDialogFields.title || '',
          CustomItemDataType.gift,
          eventId,
          null,
          Date.now(),
          RegistryItemSource.manualScanned,
        );
      }
      editRegistryItemSaveButton({
        product,
        quantity: editGiftDialogFields.qty,
        isGroupGiftingEnabled: editGiftDialogFields.isGroupGiftingEnabled,
        paymentMethod: editGiftDialogFields.paymentMethod || undefined,
        userName: editGiftDialogFields.username || undefined,
        dataExtractionSuccess: Boolean(prefilledData),
        hasAttemptedDataExtraction: hasAttemptedDataExtraction.current,
        isGetCashEquivalent: editGiftDialogFields.isGetCashEquivalentEnabled,
      });
      onClose();
    } catch (err) {
      addError(err, {
        context: 'EditGift.handleSubmit',
        paymentMethod: editGiftDialogFields.paymentMethod,
        isGroupGiftingEnabled: editGiftDialogFields.isGroupGiftingEnabled,
        productId: product?.id ?? null,
        legacyPlatformLink: product?.donationFund?.fund.platform?.link ?? null,
      });
      throw err;
    }
  };
  const minQyt = product?.isGroupGiftingEnabled ? 1 : qtyReceived || 1;
  const quantityError = qtyReceived ? receivedQuantityError() : quantityMinError();
  const isCatalogItem = product && !product?.externallyOwned && !product?.canEditProductData;

  const donationFundFundInfo = useMemo(() => product?.donationFund?.fund as DonationFundFragment | undefined, [product]);
  const defaultPlatform = donationFundInfo?.defaultPlatform;
  const currentPaymentMethod = donationFundFundInfo?.platform?.type || undefined;
  const currentPaymentMethodUsername = donationFundFundInfo?.platform?.username || undefined;

  const formik = useFormik<EditGiftDailogFields>({
    enableReinitialize: true,
    initialValues: {
      currency: isPricedProduct(product) ? getCurrencySymbolFromCurrencyCode(product.currencyCode) : DEFAULT_CURRENCY_SYMBOL,
      price: parseFloat(prefilledData?.regularPrice || '0') || (isPricedProduct(product) ? product.numberPrice.valueOf() : 0),
      qty: product?.requested || 1,
      note: product?.note || '',
      title: prefilledData?.name || product?.title || '',
      link: prefilledUrl || prefilledData?.url || product?.externalUrl || '',
      image: prefilledData?.mainImage?.url || product?.image || '',
      isGetCashEquivalentEnabled: Boolean(product?.isGetCashEquivalent),
      isGroupGiftingEnabled: !!product?.isGroupGiftingEnabled,
      username:
        donationFundFundInfo?.platform?.type && donationFundFundInfo?.platform.type !== DonationFundPlatformTypeEnum.other && donationFundFundInfo?.platform.username
          ? donationFundFundInfo?.platform?.username
          : defaultPlatform?.type !== DonationFundPlatformTypeEnum.other && defaultPlatform?.username
            ? (defaultPlatform?.username as string)
            : '',
      paymentMethod: donationFundFundInfo?.platform?.type || defaultPlatform?.type || DonationFundPlatformTypeEnum.venmo,
      mustHave: product?.mustHave || false,
    },
    onSubmit: handleSubmit,
    validationSchema: Yup.object<EditGiftDailogFields>({
      note: Yup.string().max(PARAGRAPH_MAX_LENGTH),
      title: isCatalogItem ? undefined : Yup.string().required(translateForm('required')),
      link: isCatalogItem
        ? undefined
        : Yup.string()
            .required(translateForm('required'))
            .test('is-valid-url-and-domain', linkInvalid(), value => Boolean(value) && isValidUrlAndDomain(value)),
      qty: Yup.number().min(minQyt, quantityError).integer().required(translateForm('required')).min(minQyt, quantityError),
      price: isCatalogItem
        ? undefined
        : Yup.number()
            .required(translateForm('required'))
            .moreThan(0, tq`Add a price greater than 0`),
      isGroupGiftingEnabled: Yup.boolean(),
      isGetCashEquivalentEnabled: Yup.boolean(),
      paymentMethod: Yup.mixed<DonationFundPlatformTypeEnum>().required(selectPaymentEmptyError),
      username: Yup.string().when('isGroupGiftingEnabled', {
        is: true,
        then: Yup.string()
          .matches(/^[^@]+$/g, { message: invalidUsernameIsEmailError })
          .required(invalidUsernameError),
        otherwise: Yup.string(),
      }),
      mustHave: Yup.boolean(),
    }),
  });

  const { validateField } = formik;
  const currentPriceValue = formik.values.price;

  useEffect(() => {
    if (isProductExtractionLoading || !hasAttemptedDataExtraction.current) return;
    validateField('price');
  }, [isProductExtractionLoading, currentPriceValue, validateField]);

  const setPaymentMethod = (paymentMethod?: DonationFundPlatformTypeEnum) => {
    formik.setFieldValue('paymentMethod', paymentMethod ? paymentMethod : undefined);

    if (paymentMethod !== currentPaymentMethod && !!product?.donationFund?.received) {
      setIsPaymentMethodWarningShown(true);
    } else if (isPaymentMethodWarningShown) {
      setIsPaymentMethodWarningShown(false);
    }
  };

  const setPaymentMethodUsername = (username?: string) => {
    formik.setFieldValue('username', username ? username : '');

    if (username !== currentPaymentMethodUsername && !!product?.donationFund?.received) {
      setIsPaymentMethodWarningShown(true);
    } else if (isPaymentMethodWarningShown) {
      setIsPaymentMethodWarningShown(false);
    }
  };

  const selectedPaymentMethod: Maybe<PaymentMethodOption> = paymentMethodOptions.find(option => {
    return formik && formik.values && option.value === formik.values.paymentMethod && (formik.values.paymentMethod as string) !== DonationFundPlatformTypeEnum.other;
  });

  const handleDelete = async () => {
    if (!product || !product.id) {
      return;
    }
    if (onRemove) {
      onRemove(product);
    }
  };

  const clearPhoto = () => {
    setUpdatedPhoto(undefined);
    setPhotoFilename(undefined);
  };

  const { toast } = useToast();
  const {
    open: openFilepicker,
    uploadWidget,
    isUploading,
  } = useImageUpload({
    containerId: eventId || '',
    // Filestack forced a 1:1 crop (`transformations.crop.force`); the widget does the same with this set.
    cropAspectRatio: 1,
    maxFiles: 1,
    useCase: { area: 'admin', subArea: 'registry' },
    onUpload: images => {
      const image = images[0];
      if (!image) return;
      setUpdatedPhoto(image.url);
      // addRegistryItemPhoto resolves this name inside the event's media container, which is where both
      // uploaders write, so no re-ingest is needed for either.
      setPhotoFilename(uploadedImageAssetId(image));
    },
    // useImageUpload turns off Filestack's own failure toast, so a failed upload needs one here.
    onError: error => {
      addError(error, { context: 'EditGift.photoUpload', productId: product?.id ?? null });
      toast(tq`Photo upload failed. Please try again.`);
    },
  });
  const registryItemLoading = createRegistryItemLoading || updateRegistryItemLoading || updateRegistryItemQuantityLoading || isUploading;

  useEffect(() => {
    const image = prefilledData?.mainImage?.url || product?.image;
    setUpdatedPhoto(image);
  }, [prefilledData?.mainImage?.url, product]);

  const canEditPhoto = !isCatalogItem || Boolean(formik.values.isGetCashEquivalentEnabled);

  // Reset any pending upload if photo editing becomes disallowed mid-edit
  // (e.g. user toggles Get Cash Equivalent off on a catalog item)
  useEffect(() => {
    if (!canEditPhoto) {
      setUpdatedPhoto(product?.image || undefined);
    }
  }, [canEditPhoto, product?.image]);

  const {
    onOpenDisclaimerDialog,
    onClose: onCloseDisclaimerDialog,
    isOpen: isOpenDisclaimerDialog,
    disclaimerFormik,
    agreementAccepted,
    paymentMetadata,
  } = useAppPaymentDisclaimerDialogController(eventId);

  const handleConfirm = useCallback(() => {
    if (canEditPhoto) {
      formik.setFieldValue('image', updatedPhoto);
    }
    if (!formik.isSubmitting) {
      formik.submitForm();
    }
  }, [formik, updatedPhoto, canEditPhoto]);

  const handleDisclaimerBeforeSubmit = useCallback(() => {
    const isPlatformExist = getExistingPlatform({ paymentMethod: formik.values.paymentMethod, username: formik.values.username || '' }, donationFundInfo);
    if (formik.values.isGroupGiftingEnabled && !isPlatformExist && !agreementAccepted) {
      onOpenDisclaimerDialog(createPaymentMetadata({ paymentMethod: formik.values.paymentMethod, username: formik.values.username || '' }));
    } else {
      handleConfirm();
    }
  }, [formik, agreementAccepted, onOpenDisclaimerDialog, handleConfirm, donationFundInfo]);

  const handleCancel = () => {
    formik.resetForm();
    onClose();
  };

  const handleDeleteRegistryItem = async () => {
    await handleDelete();
    onClose();
  };

  const handleTooltipClick = useCallback(
    e => {
      e.preventDefault();
      onClose();
      showCurrencyDialog();
    },
    [onClose, showCurrencyDialog],
  );

  const handleChangeFromPurchaseOption = (value: PurchaseOption) => {
    formik.setFieldValue('isGetCashEquivalentEnabled', value === 'cash');
    formik.setFieldValue('isGroupGiftingEnabled', value === 'groupGift');
  };

  return {
    formik,
    handleDelete,
    registryItemLoading,
    showCurrencyDialog,
    updatedPhoto,
    canEditPhoto,
    openFilepicker,
    uploadWidget,
    handleTooltipClick,
    handleDeleteRegistryItem,
    handleCancel,
    handleConfirm: handleDisclaimerBeforeSubmit,
    setPaymentMethod,
    setPaymentMethodUsername,
    selectedPaymentMethod,
    isPaymentMethodWarningShown,
    handleBrandClick,
    isProductExtractionLoading,
    prefilledData,
    extractDataFromUrl,
    showExtractError,
    handleChangeFromPurchaseOption,
    onCloseDisclaimerDialog,
    isOpenDisclaimerDialog,
    disclaimerFormik,
    paymentMetadata,
  };
};
